import HelpTooltip from '@/app/common/components/HelpTooltip';
import type { ChangeEvent } from 'react';
import { Col, Form, Row, Tab } from 'react-bootstrap';
import Select, { type SingleValue } from 'react-select';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import UserAgentOptions from './UserAgentOptions';
import {
  userAgentsCreator,
  type UserAgentOption
} from './userAgentCreator';

interface UserAgentValues {
  userAgent?: UserAgentOption | null;
  custom_user_agent_is_enable?: boolean;
  custom_user_agent_value?: string;
}

type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean
) => Promise<unknown>;

interface UserAgentCralwerSettingsProps {
  values: UserAgentValues;
  setFieldValue: SetFieldValue;
}

const UserAgentCralwerSettings = ({
  values,
  setFieldValue
}: UserAgentCralwerSettingsProps) => {
  const customOption = userAgentsCreator()[0];
  const customEnabled = values.custom_user_agent_is_enable === true;
  const selectedUserAgent = customEnabled
    ? customOption
    : values.userAgent ?? null;

  const handleUserAgentChange = (
    option: SingleValue<UserAgentOption>
  ): void => {
    const isCustom = option?.value === '';
    void setFieldValue('custom_user_agent_is_enable', isCustom);
    if (isCustom) {
      void setFieldValue('userAgent', {
        ...(option ?? customOption),
        value: values.custom_user_agent_value ?? ''
      });
      return;
    }

    void setFieldValue('custom_user_agent_value', '');
    void setFieldValue('userAgent', option);
  };

  const handleCustomEnabledChange = (enabled: boolean): void => {
    void setFieldValue('custom_user_agent_is_enable', enabled);
    if (enabled) {
      void setFieldValue('userAgent', {
        ...customOption,
        value: values.custom_user_agent_value ?? ''
      });
      return;
    }

    void setFieldValue('custom_user_agent_value', '');
    void setFieldValue('userAgent', customOption);
  };

  return (
    <Tab.Pane className="my-3" eventKey="crawlerUserAgent">
      <div dir="rtl" className="text-start">
        <Row className="g-3">
          <Col xs={12}>
            <div className="crawl-settings-box border rounded-2 p-3 h-100">
              <Form.Label htmlFor="user-agent-select" className="mb-1">
                <span className="d-inline-flex align-items-center">
                  انتخاب یوزر ایجنت
                  <HelpTooltip
                    html={
                      crawlTooltip.crawlSettings.crawlerUserAgentTooltips
                        .model
                    }
                  />
                </span>
              </Form.Label>
              <Select<UserAgentOption, false>
                inputId="user-agent-select"
                placeholder="انتخاب کنید"
                menuShouldScrollIntoView={true}
                closeMenuOnSelect={true}
                isSearchable={false}
                onChange={handleUserAgentChange}
                options={userAgentsCreator()}
                classNamePrefix="react-select"
                value={selectedUserAgent}
                className="rounded-end-0 text-capitalize p-0 m-0 text-end"
                maxMenuHeight={700}
                components={{ Option: UserAgentOptions }}
              />
            </div>
          </Col>
          <Col xs={12}>
            <div className="crawl-settings-box border rounded-2 p-3 h-100">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <Form.Label
                  htmlFor="custom_user_agent_is_enable"
                  className="mb-0"
                >
                  <span className="d-inline-flex align-items-center">
                    یوزر ایجنت سفارشی
                    <HelpTooltip
                      html={
                        crawlTooltip.crawlSettings.crawlerUserAgentTooltips
                          .custom
                      }
                    />
                  </span>
                </Form.Label>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <span className="fs--1 text-700">
                    {customEnabled ? 'بله' : 'خیر'}
                  </span>
                  <Form.Check
                    type="switch"
                    id="custom_user_agent_is_enable"
                    name="custom_user_agent_is_enable"
                    checked={customEnabled}
                    onChange={event =>
                      handleCustomEnabledChange(event.target.checked)
                    }
                    aria-label="یوزر ایجنت سفارشی"
                  />
                </div>
              </div>
              <Form.Label
                htmlFor="custom_user_agent_value"
                className="mb-1 mt-3"
              >
                <span className="d-inline-flex align-items-center">
                  مقدار یوزر ایجنت
                  <HelpTooltip
                    html={
                      crawlTooltip.crawlSettings.crawlerUserAgentTooltips
                        .value
                    }
                  />
                </span>
              </Form.Label>
              <Form.Control
                id="custom_user_agent_value"
                name="custom_user_agent_value"
                type="text"
                disabled={!customEnabled}
                style={{ direction: 'ltr' }}
                value={values.custom_user_agent_value ?? ''}
                placeholder="Mozilla/5.0 ..."
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  void setFieldValue('custom_user_agent_value', value);
                  void setFieldValue('userAgent', {
                    ...customOption,
                    value
                  });
                }}
              />
            </div>
          </Col>
        </Row>
      </div>
    </Tab.Pane>
  );
};

export default UserAgentCralwerSettings;
